<template>
  <section class="manager-history -project-applications">
    <ContentBox class="content-box">

      <div class="row mb-s _list-title">
        <div class="col-20">
          <h4 class="h4 mb-0 font-component-headline">{{ $t('managerApplicationHistory.headline') }}</h4>
        </div>
      </div>

      <div class="_list-wrapper">

        <div class="row _list-head">
          <div class="col-10">
            <a class="label" @click="handleSort('Project.title')">
              {{ $t('managerApplicationHistory.projectName') }}
              <Icon v-if="selectedSort.value === 'Project.title'" :class="[{ '-rotate-180': !selectedSort.orderDesc }, '-size-22']">
                <ChevronFullSVG></ChevronFullSVG>
              </Icon>
            </a>
          </div>
          <div class="col-7 col-offset-1">
            <a class="label" @click="handleSort('status')">
              {{ $t('managerApplicationHistory.status') }}
              <Icon v-if="selectedSort.value === 'status'" :class="[{ '-rotate-180': !selectedSort.orderDesc }, '-size-22']">
                <ChevronFullSVG></ChevronFullSVG>
              </Icon>
            </a>
          </div>
          <div class="col-4 col-offset-1">
            <a class="label" @click="handleSort('createdAt')">
              {{ $t('managerApplicationHistory.createdAt') }}
              <Icon v-if="selectedSort.value === 'createdAt'" :class="[{ '-rotate-180': !selectedSort.orderDesc }, '-size-22']">
                <ChevronFullSVG></ChevronFullSVG>
              </Icon>
            </a>
          </div>
        </div>

        <div v-if="applications.length === 0" class="row">
          <div class="col-24 pt-xs pb-xs">
            <span class="font-weight-semibold color-grey-300">{{ $t('managerApplicationHistory.noApplications') }}</span>
          </div>
        </div>
        <template v-else>
          <div class="_list-items">
            <div
              class="row _item"
              v-for="application in showApplications"
              :key="application.id"
              @click="openApplicationDetail(application.ProjectId, application.id)"
            >
              <div class="col-10">
                <span class="font-weight-semibold text-truncate">{{ application.Project.title }}</span>
              </div>
              <div class="col-7 col-offset-1">
                <span class="font-weight-medium text-truncate">{{ $t(`projectApplications.fields.${application.status}.label`) }}</span>
              </div>
              <div class="col-4 col-offset-1">
                <span class="font-weight-medium">{{ application.createdAt | formatDate }}</span>
                &nbsp;
                <span class="font-weight-medium color-grey-400">({{ application.createdAt | formatTime }})</span>
              </div>
              <div class="col-1 pos-relative text-right">
                <FlyoutNav class="mt-xs" :ref="`flyout-projectApplication-${application.id}`">
                  <div>
                    <a @click.stop="openProjectDetail(application.ProjectId)">{{ $t('managerApplicationHistory.actions.project') }}</a>
                    <a @click.stop="openApplicationDetail(application.ProjectId, application.id)">{{ $t('managerApplicationHistory.actions.application') }}</a>
                    <a @click.stop="openComments(application.ManagerUuid, application.id)">{{ $t('managerApplicationHistory.actions.comments') }}</a>
                  </div>
                </FlyoutNav>
                <FlyoutTrigger :flyoutEl="`flyout-projectApplication-${application.id}`" :parentRefs="$refs">
                  <Icon class="-size-40 -outlined p-xxs-less -bg-white">
                    <DotsSVG></DotsSVG>
                  </Icon>
                </FlyoutTrigger>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div v-if="showApplications.length < applications.length || showAll" class="text-center mt-xs">
        <Button class="color-brand-orange" @click.native="toggleShowAll">
          <span v-if="showAll">
            {{ $t('managerApplicationHistory.showLess') }}
          </span>
          <span v-else>
            {{ $t('managerApplicationHistory.showAll') }}
          </span>
          <Icon :class="[{ '-rotate-180': showAll }, '-size-22']">
            <ChevronSVG></ChevronSVG>
          </Icon>
        </Button>
      </div>
    </ContentBox>
  </section>
</template>

<script>
import _ from 'lodash';
import { parseDates } from '@/mixins';
import Button from '@/components/atoms/Button.vue';
import ContentBox from '@/components/elements/ContentBox.vue';
import Icon from '@/components/atoms/Icon.vue';
import ChevronFullSVG from '@/assets/icons/chevron-full.svg';
import ChevronSVG from '@/assets/icons/chevron.svg';
import DotsSVG from '@/assets/icons/dots.svg';
import FlyoutNav from '@/components/elements/FlyoutNav.vue';
import FlyoutTrigger from '@/components/elements/FlyoutTrigger.vue';

export default {
  name: 'ManagerApplicationHistory',

  mixins: [parseDates],

  props: {
    applications: Array,
  },

  data() {
    return {
      showAll: false,
      limit: 5,
      selectedSort: {
        value: 'createdAt',
        orderDesc: true,
      },
    };
  },

  computed: {
    showApplications() {
      const sortCopy = this.applications;
      return sortCopy.sort((a, b) => {
        let aValue = _.get(a, this.selectedSort.value);
        let bValue = _.get(b, this.selectedSort.value);

        if (this.selectedSort.value === 'createdAt') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        if (this.selectedSort.value === 'status') {
          aValue = this.$t(`projectApplications.fields.${aValue}.label`);
          bValue = this.$t(`projectApplications.fields.${bValue}.label`);
        }

        if (this.selectedSort.orderDesc) {
          return (bValue > aValue) ? 1 : -1;
        }

        return (aValue > bValue) ? 1 : -1;
      }).slice(0, (!this.showAll) ? this.limit : this.applications.length);
    },
  },

  methods: {
    toggleShowAll() {
      this.showAll = !this.showAll;
    },

    openComments(managerUuid, applicationId) {
      this.$eventBus.$emit('open-modal-comments', {
        manager: managerUuid,
        commentableType: 'ProjectApplication',
        commentableId: applicationId,
      });
    },

    openProjectDetail(id) {
      this.$router.push({
        name: 'ProjectDetail',
        params: {
          id,
          lang: this.$i18n.locale,
        },
      });
    },

    openApplicationDetail(projectId, applicationId) {
      this.$router.push({
        name: 'ProjectApplications',
        params: {
          projectId,
          applicationId,
          lang: this.$i18n.locale,
        },
      });
    },

    handleSort(value) {
      if (this.selectedSort.value === value) {
        this.selectedSort.orderDesc = !this.selectedSort.orderDesc;
      } else {
        this.selectedSort.value = value;
        this.selectedSort.orderDesc = false;
      }
    },
  },

  components: {
    ContentBox,
    Button,
    Icon,
    ChevronFullSVG,
    ChevronSVG,
    FlyoutNav,
    FlyoutTrigger,
    DotsSVG,
  },
};
</script>

<style scoped lang="scss" src="@/sass/08_modules/manager-history.scss"></style>
